/**
 * Apollo-aware detection for the subscription auth-recovery flow (Fix 3).
 *
 * `auth-recovery.ts` holds the dependency-free *decision* logic; this module is
 * the thin adapter that knows about Apollo's concrete error wrappers
 * (`CombinedGraphQLErrors`, `CombinedProtocolErrors`) and `getMainDefinition`.
 * It is kept separate (and small) so it can be unit-tested by constructing
 * REAL Apollo v4 error instances — that is what removes the "we can't prove
 * which wrapper Apollo uses for a WS rejection" uncertainty: the test exercises
 * both wrappers with the server's actual error payload shape.
 */
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
} from "@apollo/client/errors";
import { getMainDefinition } from "@apollo/client/utilities";
import {
  classifyRefreshErrorCodes,
  classifyRefreshFailure,
  containsUnauthorizedUser,
} from "./auth-recovery";

const UNKNOWN_GRAPHQL_ERROR_CODE = "unknown";

/**
 * Only these operations are covered by the root loader's redirects in
 * `app/utils/space-routing.ts`. Other operations can emit the same mindspace
 * codes without redirecting, so browser failures remain reportable there.
 */
const ROOT_LOADER_SPACE_OPERATIONS = new Set([
  // eslint-disable-next-line lingui/no-unlocalized-strings -- GraphQL operation name, not user-facing copy
  "ContextNotAuthenticated",
  // eslint-disable-next-line lingui/no-unlocalized-strings -- GraphQL operation name, not user-facing copy
  "ContextAuthenticated",
]);

/**
 * These are the root-loader routing outcomes handled by
 * `resolveUnknownSpaceRedirect` and `resolveNoSpaceAccessRedirect`. The list is
 * deliberately narrow: every other `not_found.*` code remains reportable.
 */
const HANDLED_MINDSPACE_ROUTING_CODES = new Set([
  // eslint-disable-next-line lingui/no-unlocalized-strings -- wire error codes, not user-facing copy
  "not_found.mindspace",
  "forbidden.mindspace",
]);

/** Exact server route outcomes; a present wire code always takes precedence. */
export function isExpectedRouteGraphQLError(
  error: CombinedGraphQLErrors["errors"][number],
): boolean {
  if (error.extensions && "code" in error.extensions) {
    const code = error.extensions.code;
    return (
      typeof code === "string" &&
      (HANDLED_MINDSPACE_ROUTING_CODES.has(code) || code === "not_found.item")
    );
  }

  // AppError can arrive without extensions (notably Section.item).
  return /^(?:not_found\.mindspace|not_found\.item|forbidden\.mindspace):/.test(
    error.message,
  );
}

function graphQLErrorCode(
  error: CombinedGraphQLErrors["errors"][number],
): string {
  const code = error.extensions?.code;
  return typeof code === "string" && code.length > 0
    ? code
    : UNKNOWN_GRAPHQL_ERROR_CODE;
}

function isExpectedAuthError(
  error: CombinedGraphQLErrors["errors"][number],
): boolean {
  const message = typeof error.message === "string" ? error.message : "";
  return (
    error.extensions?.code === "unauthorized.user" ||
    error.extensions?.code === "forbidden.invalid_access" ||
    error.extensions?.code ===
      "internal_service.platform_principal_unverified" ||
    message.includes("user is not authorized") ||
    message.includes("user can't access this")
  );
}

/**
 * Report a combined response when at least one constituent error is not an
 * expected auth outcome. The two handled mindspace routing outcomes are also
 * suppressed for browser root-loader redirects. On the server, expected route
 * outcomes from any operation are logged by the entry handler at WARNING.
 */
export function shouldReportCombinedGraphQLErrors(
  operationName: unknown,
  error: CombinedGraphQLErrors,
): boolean {
  const handlesMindspaceRouting =
    typeof operationName === "string" &&
    ROOT_LOADER_SPACE_OPERATIONS.has(operationName);

  return error.errors.some(
    (graphQLError) =>
      !isExpectedAuthError(graphQLError) &&
      !(
        typeof window === "undefined" &&
        isExpectedRouteGraphQLError(graphQLError)
      ) &&
      !(
        handlesMindspaceRouting &&
        HANDLED_MINDSPACE_ROUTING_CODES.has(graphQLErrorCode(graphQLError))
      ),
  );
}

/** Stable, sorted wire codes for Sentry tags, context, and grouping. */
export function combinedGraphQLErrorCodes(
  error: CombinedGraphQLErrors,
): string[] {
  return [...new Set(error.errors.map(graphQLErrorCode))].sort();
}

/**
 * Group GraphQL responses by operation and wire code, never by message: error
 * messages can contain probed mindspace identifiers and would create one issue
 * per scanner subdomain. Missing or malformed codes use a stable fallback.
 */
export function graphQLErrorFingerprint(
  operationName: unknown,
  error: CombinedGraphQLErrors,
): string[] {
  const stableOperationName =
    typeof operationName === "string" && operationName.length > 0
      ? operationName
      : "anonymous";
  return ["graphql", stableOperationName, ...combinedGraphQLErrorCodes(error)];
}

/** True when the operation document is a GraphQL subscription. */
export function isSubscriptionDocument(query: unknown): boolean {
  try {
    const definition = getMainDefinition(query as never);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  } catch {
    return false;
  }
}

/**
 * Detect an `unauthorized.user` error inside any of Apollo's error wrappers.
 * Handles both the per-event GraphQL-errors shape and the terminating protocol
 * shape (which is how a subscription that throws in `subscribe` arrives), and
 * matches on the error code OR the message text so it holds even if the wrapper
 * drops `extensions`.
 */
export function extractUnauthorizedFromError(error: unknown): boolean {
  if (CombinedGraphQLErrors.is(error)) {
    return containsUnauthorizedUser(
      error.errors.map((e) => ({
        code: e.extensions?.code,
        message: e.message,
      })),
    );
  }
  if (CombinedProtocolErrors.is(error)) {
    return containsUnauthorizedUser(
      error.errors.map((e) => ({
        code: e.extensions?.code,
        message: e.message,
      })),
    );
  }
  return false;
}

/**
 * The wire code the OpenAI/Anthropic/Perplexity provider boundary emits when
 * the account hits its quota cap (Fix A, stage 1: `mapProviderError` →
 * `rateLimited('insufficient_quota')`). It survives the RPC seam + GraphQL
 * `maskError` intact and arrives in the browser as `extensions.code`.
 */
// eslint-disable-next-line lingui/no-unlocalized-strings -- wire error code, not user-facing copy
const AI_QUOTA_CODE = "rate_limited.insufficient_quota";
// eslint-disable-next-line lingui/no-unlocalized-strings -- wire error code, not user-facing copy
const SANDBOX_MESSAGE_CAP_CODE = "rate_limited.sandbox_message_cap";

/**
 * Detect the AI provider quota error (`rate_limited.insufficient_quota`) inside
 * Apollo's error wrappers, using the same unwrap pattern as the unauthorized
 * detection above so it holds for both the per-event GraphQL-errors shape and
 * the terminating protocol shape. A generic error (a 500, a plain Error, a
 * different code) returns false so only the quota case gets the "AI taking a
 * short break" treatment; everything else keeps today's generic handling.
 */
export function isAiQuotaError(error: unknown): boolean {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some((e) => e.extensions?.code === AI_QUOTA_CODE);
  }
  if (CombinedProtocolErrors.is(error)) {
    return error.errors.some((e) => e.extensions?.code === AI_QUOTA_CODE);
  }
  return false;
}

export function isSandboxMessageCapError(error: unknown): boolean {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some(
      (e) => e.extensions?.code === SANDBOX_MESSAGE_CAP_CODE,
    );
  }
  if (CombinedProtocolErrors.is(error)) {
    return error.errors.some(
      (e) => e.extensions?.code === SANDBOX_MESSAGE_CAP_CODE,
    );
  }
  return false;
}

/**
 * Classify a failure of the SSR token-refresh mutation (`RefreshAccessToken`)
 * into `invalid` (log the user out) vs `transient` (keep the session, surface a
 * retryable error). This is the Apollo-aware adapter over the pure
 * `classifyRefreshErrorCodes` decision logic — it unwraps whichever error shape
 * Apollo v4's `mutate` surfaces:
 *
 *  - `CombinedGraphQLErrors` / `CombinedProtocolErrors` -> inspect each error's
 *    `extensions.code` + message (a `bad_request.invalid_token` is `invalid`; an
 *    `internal_service` 5xx is `transient`).
 *  - A `ServerError` (non-2xx HTTP, e.g. a raw 500), a network error, a timeout,
 *    or any other thrown value -> `transient`. We only ever log out on a
 *    positively-identified invalid token; an infra failure keeps the session.
 *
 * This is the fix for the FOX-3654 amplifier: `ensure-authenticated.server.ts`
 * previously treated ANY refresh error (including a backend 5xx) as "session
 * dead" and redirected to `/logout`, turning a transient backend blip into a
 * mass logout.
 */
export function classifyRefreshError(error: unknown): "invalid" | "transient" {
  if (CombinedGraphQLErrors.is(error)) {
    return classifyRefreshErrorCodes(
      error.errors.map((e) => ({
        code: e.extensions?.code,
        message: e.message,
      })),
    );
  }
  if (CombinedProtocolErrors.is(error)) {
    return classifyRefreshErrorCodes(
      error.errors.map((e) => ({
        code: e.extensions?.code,
        message: e.message,
      })),
    );
  }
  // Not a GraphQL-errors wrapper (ServerError/network/timeout/plain throw).
  // Only an explicit invalid/missing-token message is `invalid`; an HTTP status
  // alone is not, since a proxy can answer 401/403 for the backend (FOX-6551).
  return classifyRefreshFailure(error);
}

/**
 * The single predicate the error link uses: only subscription operations ride
 * the WebSocket, so only an unauthorized error on a subscription should trigger
 * a forced reconnect.
 */
export function shouldRecoverSubscriptionAuth(
  error: unknown,
  query: unknown,
): boolean {
  return isSubscriptionDocument(query) && extractUnauthorizedFromError(error);
}
